// A hidden, always-present <div id="__print_sandbox"> with a header + a cloned copy of `.mainContent`.
import { useEffect } from "react";
import { createGlobalStyle, ThemeProvider } from "styled-components";
import { createRoot, Root } from "react-dom/client";
import { Logo } from "../components/logo";
import { lightTheme } from "../style/index";

const PrintCSS = createGlobalStyle`
  /* Screen: hide the sandbox */
  @media screen {
    #__print_sandbox { display: none !important; }
  }

  /* Print: show only the sandbox */
  @media print {
    html, body {
      background: #fff !important;
      color: #000 !important;
      -webkit-print-color-adjust: exact !important;
      print-color-adjust: exact !important;
      margin: 0 !important; padding: 0 !important;
      height: auto !important; overflow: visible !important;
      contain: none !important; content-visibility: visible !important;
    }

    /* Hide everything except the sandbox */
    body > *:not(#__print_sandbox) { display: none !important; }


    /* Header styling */
    .print-header {
      padding: 10mm 0 6mm;
      border-bottom: 1px solid #ddd;
    }
    .print-header-inner {
      display: flex; flex-direction: column; align-items: center; gap: 2mm;
    }
    .print-logo-host svg { height: 8mm; width: auto; }



     #__print_sandbox {
       font-size: 10pt;
     }

    /* Page margins & friendlier breaks */
    @page { margin: 12mm; }
    // #__print_sandbox h1, #__print_sandbox h2, #__print_sandbox h3 { break-after: avoid-page !important; }
    #__print_sandbox table, #__print_sandbox pre, #__print_sandbox blockquote { break-inside: avoid-page !important; }
    }

    /* kill trailing whitespace that can trigger an extra page */
    #__print_sandbox .mainContent {
      padding-bottom: 0 !important;
      margin-bottom: 0 !important;
    }
    #__print_sandbox .print-body {
      padding-bottom: 0 !important;
      margin-bottom: 0 !important;
    }
    #__print_sandbox .mainContent > :last-child {
      margin-bottom: 0 !important;
      padding-bottom: 0 !important;
    }
`;

export default function PrintSandbox({
  selector = ".mainContent",
}: {
  selector?: string;
}) {
  useEffect(() => {
    let logoRoot: Root | null = null;
    let debounceTimer: ReturnType<typeof setTimeout>;

    const ensureSandbox = () => {
      let box = document.querySelector("#__print_sandbox");
      if (!box) {
        box = document.createElement("div");
        box.id = "__print_sandbox";

        const header = document.createElement("div");
        header.className = "print-header";

        const headerInner = document.createElement("div");
        headerInner.className = "print-header-inner";

        const logo = document.createElement("div");
        logo.className = "print-logo-host";

        headerInner.append(logo);
        header.append(headerInner);

        // Body for cloned content
        const body = document.createElement("div");
        body.className = "print-body";

        box.append(header, body);
        document.body.append(box);

        logoRoot = createRoot(logo);
        logoRoot.render(
          <ThemeProvider theme={lightTheme}>
            <Logo darkMode={false} />
          </ThemeProvider>
        );
      }
      // We created this HTML so we know it's a valid HTMLElement.
      // oxlint-disable-next-line typescript/no-unsafe-type-assertion
      return document.querySelector("#__print_sandbox") as HTMLElement;
    };

    const getBody = () =>
      document.querySelector("#__print_sandbox .print-body");

    const sync = () => {
      // Re-select target each time in case it was re-mounted
      // oxlint-disable-next-line typescript/no-unsafe-type-assertion
      const target = document.querySelector(selector) as HTMLElement | null;
      if (!target) return;

      const box = ensureSandbox();
      const body = getBody();
      if (!box || !body) return;

      // Clone the live content into the body (no header rebuild)
      // oxlint-disable-next-line typescript/no-unsafe-type-assertion
      const clone = target.cloneNode(true) as HTMLElement;
      clone.style.maxWidth = "none";
      clone.style.width = "auto";
      clone.style.margin = "0";
      body.replaceChildren(clone);
    };

    sync();

    // Observe changes under the target, debounce to 100ms
    // oxlint-disable-next-line typescript/no-unsafe-type-assertion
    const targetNode = document.querySelector(selector) as HTMLElement | null;
    if (!targetNode) return undefined;

    const mo = new MutationObserver(() => {
      clearTimeout(debounceTimer);
      debounceTimer = setTimeout(sync, 100);
    });
    mo.observe(targetNode, {
      subtree: true,
      childList: true,
      attributes: true,
    });

    return () => {
      mo.disconnect();
      clearTimeout(debounceTimer);
      const box = document.querySelector("#__print_sandbox");
      if (box) {
        box.remove();
      }
    };
  }, [selector]);

  return <PrintCSS />;
}
